/**
 * The client × period matrix.
 *
 * EQ_Practice_Scope_Rev2.md §6.3: this is a **validated** design, confirmed
 * directly against 27 real SmartPractice screenshots as the practice's actual
 * working pattern — "rebuild it, don't redesign it". Every one of the UX
 * decisions the docs record as deliberate is carried across:
 *
 *   - current + previous period by default, with "Show all periods" one click away
 *   - category tabs for VAT201 only
 *   - per-cell notes with a bulk-fetched count badge, not N+1 queries
 *   - a reference-number column per obligation type (VAT no. / PAYE no. / …)
 *   - COIDA_RETURN's 4 sequential milestones replacing the status control
 *
 * All of those decisions are made server-side in `server/src/matrix-view.ts`.
 * This file renders them and does not re-derive any.
 *
 * Filter state lives in the URL, as it did in the Flask build, so a filtered
 * view is a link someone can be sent.
 */

import { useCallback, useEffect, useState } from 'react'
import { useParams, useSearchParams } from 'react-router-dom'
import {
  api,
  ApiError,
  type AuthState,
  type MatrixCell,
  type MatrixResponse,
  type ObligationTypeInUse,
} from '../api'
import { AppShell } from '../components/AppShell'
import { StatusBadge } from '../components/StatusBadge'
import { Notice, type NoticeState } from '../components/Notice'
import { EmptyState, ErrorState, LoadingState } from '../components/States'
import { NotesPanel } from '../components/NotesPanel'
import { StatusControl } from './DueAndOverduePage'
import { statusStyle } from '../status'
import { AllocationLabel } from '../components/AllocationLabel'
import { downloadCsv } from '../csv'

export function MatrixPage({
  obligationTypes,
  auth,
}: {
  obligationTypes: ObligationTypeInUse[]
  auth: AuthState
}) {
  const { code = '' } = useParams()
  const [params, setParams] = useSearchParams()

  const category = params.get('category')
  const search = params.get('q') ?? ''
  // Legacy free-text filter: still honoured in a shared link, no longer offered.
  const assignedTo = params.get('assignedTo') ?? ''
  const allocatedTo = params.get('who') ?? ''
  const status = params.get('status') ?? ''
  const isManager = auth.mode === 'anonymous' || auth.user?.role === 'manager'
  const periods = params.get('periods') === 'all' ? 'all' : 'recent'
  const asOf = params.get('asOf') ?? undefined

  const [data, setData] = useState<MatrixResponse | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [notice, setNotice] = useState<NoticeState | null>(null)
  const [searchDraft, setSearchDraft] = useState(search)
  const [notesFor, setNotesFor] = useState<string | null>(null)

  const load = useCallback(async () => {
    try {
      setError(null)
      setData(
        await api.matrix(code, { category, q: search, assignedTo, allocatedTo, status, periods, asOf })
      )
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    }
  }, [code, category, search, assignedTo, allocatedTo, status, periods, asOf])

  useEffect(() => {
    void load()
  }, [load])

  useEffect(() => {
    setSearchDraft(search)
  }, [search, code])

  /** Merge into the existing query string, so one filter never clears another. */
  const updateParams = (changes: Record<string, string | null>) => {
    const next = new URLSearchParams(params)
    for (const [key, value] of Object.entries(changes)) {
      if (value === null || value === '') next.delete(key)
      else next.set(key, value)
    }
    setParams(next)
  }

  async function act(what: () => Promise<string>, onDone?: () => void) {
    try {
      setNotice({ tone: 'success', message: await what() })
      onDone?.()
      await load()
    } catch (e) {
      setNotice({
        tone: e instanceof ApiError && e.status === 409 ? 'warning' : 'danger',
        message: e instanceof Error ? e.message : String(e),
      })
    }
  }

  const hasFilters = Boolean(search || assignedTo || allocatedTo || status)

  function exportCsv() {
    if (!data) return
    const header = [
      'Client',
      ...(data.referenceColumn ? [data.referenceColumn.label] : []),
      'Allocated to',
      ...data.periods.map((p) => `${p.periodStart} to ${p.periodEnd}`),
    ]
    const rows = data.rows.map((row) => [
      row.clientName,
      ...(data.referenceColumn ? [row.referenceNumber ?? ''] : []),
      row.allocation?.name ?? '',
      ...data.periods.map((p) => {
        const cell = row.cells[p.key]
        if (!cell) return ''
        const label = statusStyle(cell.status).label
        return cell.dueDate ? `${label} (due ${cell.dueDate})` : label
      }),
    ])
    downloadCsv(`${data.code}-${data.filters.asOfDate}.csv`, header, rows)
  }

  return (
    <AppShell
      obligationTypes={obligationTypes}
      auth={auth}
      eyebrow="Obligation type"
      title={
        <>
          {code}
          {data && (
            <span className="ml-3 font-heading text-[19px] font-normal text-eq-neutral">
              {data.description}
            </span>
          )}
        </>
      }
      subtitle="One row per client, one column per period. A blank cell means this client has no period for that column — a different filing cadence, not a missing filing."
    >
      {notice && <Notice notice={notice} onDismiss={() => setNotice(null)} />}
      {error && <ErrorState message={error} />}
      {!error && !data && <LoadingState what="the matrix" />}

      {data && (
        <>
          {data.categoryTabs && (
            <nav className="mb-4 flex gap-1 border-b border-eq-border">
              {data.categoryTabs.map((tab) => (
                <button
                  key={tab.key}
                  type="button"
                  onClick={() => updateParams({ category: tab.key === 'all' ? null : tab.key })}
                  className={[
                    '-mb-px border-b-2 px-3.5 py-2 font-heading text-[13px]',
                    tab.active
                      ? 'border-eq-navy font-bold text-eq-navy'
                      : 'border-transparent text-eq-neutral hover:text-eq-deep',
                  ].join(' ')}
                >
                  {tab.label}
                </button>
              ))}
            </nav>
          )}

          <div className="mb-4 flex flex-wrap items-center gap-3">
            <form
              className="flex items-center gap-2"
              onSubmit={(event) => {
                event.preventDefault()
                updateParams({ q: searchDraft.trim() })
              }}
            >
              <input
                type="search"
                className="eq-input min-w-[16rem]"
                placeholder="Search client name"
                value={searchDraft}
                onChange={(event) => setSearchDraft(event.target.value)}
                aria-label="Search client name"
              />
              <button type="submit" className="eq-btn-secondary py-2">
                Search
              </button>
            </form>

            <select
              className="eq-input"
              value={allocatedTo}
              onChange={(event) => updateParams({ who: event.target.value })}
              aria-label="Allocated to"
            >
              <option value="">Everyone</option>
              {auth.user && <option value={auth.user.id}>My work</option>}
              <option value="unallocated">Unallocated</option>
              {data.staff
                .filter((person) => person.id !== auth.user?.id)
                .map((person) => (
                  <option key={person.id} value={person.id}>
                    {person.name}
                  </option>
                ))}
            </select>

            <select
              className="eq-input"
              value={status}
              onChange={(event) => updateParams({ status: event.target.value })}
              aria-label="Status"
            >
              <option value="">Any status</option>
              {data.statusOptions.map((option) => (
                <option key={option} value={option}>
                  {statusStyle(option).label}
                </option>
              ))}
            </select>

            {hasFilters && (
              <button
                type="button"
                className="eq-btn-tertiary py-2"
                onClick={() => updateParams({ q: null, assignedTo: null, who: null, status: null })}
              >
                Clear filters
              </button>
            )}

            <span className="ml-auto flex gap-2 print:hidden">
              <button type="button" className="eq-btn-tertiary py-2" onClick={exportCsv}>
                Export CSV
              </button>
              <button type="button" className="eq-btn-tertiary py-2" onClick={() => window.print()}>
                Print
              </button>
            </span>
          </div>

          <p className="mb-3 text-[12.5px] text-eq-helper">
            {periods === 'all' ? 'Showing all periods' : 'Showing current and previous period only'}
            {' · '}
            <button
              type="button"
              className="font-semibold text-eq-deep underline"
              onClick={() => updateParams({ periods: periods === 'all' ? null : 'all' })}
            >
              {periods === 'all' ? 'Show current + previous only' : 'Show all periods'}
            </button>
          </p>

          {data.periods.length === 0 ? (
            <EmptyState>
              {hasFilters || category
                ? 'No clients match this filter.'
                : data.rawHasRows && periods !== 'all'
                  ? 'No periods have started yet in the current and previous window. Show all periods to see the rest.'
                  : 'No clients currently carry this obligation.'}
            </EmptyState>
          ) : (
            <div className="overflow-auto rounded-eq-md border border-eq-border bg-white shadow-eq-card">
              <table className="eq-table">
                <thead>
                  <tr>
                    <th className="min-w-[14rem]">Client</th>
                    {data.referenceColumn && (
                      <th className="whitespace-nowrap">{data.referenceColumn.label}</th>
                    )}
                    <th className="whitespace-nowrap">Allocated to</th>
                    {data.periods.map((period) => (
                      <th key={period.key} className="whitespace-nowrap text-center">
                        {period.periodStart}
                        <span className="block font-body text-[11px] font-normal normal-case tracking-normal text-eq-helper">
                          to {period.periodEnd}
                        </span>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {data.rows.map((row) => (
                    <tr key={row.clientId} className="align-top">
                      <td className="font-medium">{row.clientName}</td>
                      {data.referenceColumn && (
                        <td className="whitespace-nowrap tabular-nums text-eq-neutral">
                          {row.referenceNumber ?? '—'}
                        </td>
                      )}
                      <td className="whitespace-nowrap text-eq-neutral">
                        {isManager && data.staff.length > 0 ? (
                          <AllocationPicker
                            value={row.allocation}
                            staff={data.staff}
                            onChange={(userId) =>
                              void act(async () => {
                                await api.allocateObligation(row.obligationId, userId)
                                const who = data.staff.find((p) => p.id === userId)?.name
                                return `${row.clientName} — ${code} ${who ? `allocated to ${who}` : 'unallocated'}.`
                              })
                            }
                          />
                        ) : (
                          <AllocationLabel allocation={row.allocation} />
                        )}
                      </td>
                      {data.periods.map((period) => {
                        const cell = row.cells[period.key]
                        return (
                          <td key={period.key} className="py-2 text-center align-top">
                            {cell ? (
                              <MatrixCellView
                                cell={cell}
                                isCoida={data.isCoida}
                                onSetStatus={(next, reason) =>
                                  void act(async () => {
                                    await api.setObligationPeriodStatus(cell.itemId, next, reason)
                                    return `${row.clientName} — set to ${statusStyle(next).label.toLowerCase()}.`
                                  })
                                }
                                onTickMilestone={(milestone) =>
                                  void act(async () => {
                                    const done = await api.tickCoidaMilestone(cell.itemId, milestone)
                                    return `${row.clientName} — ${done.label} marked done.`
                                  })
                                }
                                onOpenNotes={() => setNotesFor(cell.itemId)}
                              />
                            ) : (
                              <span className="text-eq-border" aria-label="No period">
                                —
                              </span>
                            )}
                          </td>
                        )
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {notesFor && (
        <NotesPanel
          obligationPeriodId={notesFor}
          auth={auth}
          onClose={() => setNotesFor(null)}
          onAdded={() => void load()}
        />
      )}
    </AppShell>
  )
}

function MatrixCellView({
  cell,
  isCoida,
  onSetStatus,
  onTickMilestone,
  onOpenNotes,
}: {
  cell: MatrixCell
  isCoida: boolean
  onSetStatus: (status: string, reason?: string) => void
  onTickMilestone: (milestone: string) => void
  onOpenNotes: () => void
}) {
  return (
    <div className="mx-auto flex w-max min-w-[11rem] flex-col items-start gap-1.5 text-left">
      {isCoida ? (
        <>
          {cell.dueDate && <CellDate label="due" value={cell.dueDate} />}
          <ul className="w-full space-y-1">
            {cell.coidaMilestones?.map((milestone) => (
              <li key={milestone.key} className="flex items-center gap-2 text-[12.5px]">
                <input
                  type="checkbox"
                  readOnly
                  checked={milestone.date !== null}
                  aria-label={milestone.label}
                  className="h-3.5 w-3.5 accent-eq-navy"
                />
                <span className={milestone.date ? 'text-eq-success' : 'text-eq-neutral'}>
                  {milestone.label}
                </span>
                {milestone.date && (
                  <span className="tabular-nums text-eq-helper">{milestone.date}</span>
                )}
                {/* Exactly one milestone per cell is eligible; the rest are
                    either done or not reachable yet. Enforced server-side --
                    this only avoids offering a click that would be refused. */}
                {milestone.isNext && (
                  <button
                    type="button"
                    className="eq-btn-sm border-[1.5px] border-eq-navy text-eq-navy hover:bg-eq-navy hover:text-white"
                    onClick={() => onTickMilestone(milestone.key)}
                  >
                    Mark done
                  </button>
                )}
              </li>
            ))}
          </ul>
        </>
      ) : (
        <>
          <StatusBadge status={cell.status} />
          {cell.dueDate && <CellDate label="due" value={cell.dueDate} />}
          {cell.forReviewDate && <CellDate label="for review" value={cell.forReviewDate} />}
          {cell.completedDate && <CellDate label="completed" value={cell.completedDate} />}
          {cell.notApplicableReason && (
            <span className="max-w-[13rem] text-[12px] italic text-eq-helper" title={cell.notApplicableReason}>
              {cell.notApplicableDate}: {cell.notApplicableReason}
            </span>
          )}
          {cell.hasAllocationOverride && (
            <AllocationLabel allocation={cell.allocation} override />
          )}
          <span className="print:hidden">
            <StatusControl nextStatuses={cell.nextStatuses} onChange={onSetStatus} />
          </span>
        </>
      )}

      <button
        type="button"
        onClick={onOpenNotes}
        className={[
          'text-[12.5px] underline',
          cell.noteCount > 0 ? 'font-semibold text-eq-deep' : 'text-eq-helper',
        ].join(' ')}
      >
        Notes{cell.noteCount > 0 ? ` (${cell.noteCount})` : ''}
      </button>
    </div>
  )
}

function CellDate({ label, value }: { label: string; value: string }) {
  return (
    <span className="text-[12.5px] text-eq-helper">
      {label} <span className="tabular-nums">{value}</span>
    </span>
  )
}

/**
 * A manager's standing allocation for this client's obligation (D3). Imported
 * initials that resolve to a person show as that person with a "confirm" link
 * that makes it explicit; unmatched initials show as "?".
 */
function AllocationPicker({
  value,
  staff,
  onChange,
}: {
  value: MatrixResponse['rows'][number]['allocation']
  staff: MatrixResponse['staff']
  onChange: (userId: string | null) => void
}) {
  const current = value?.userId ?? ''
  return (
    <span className="flex items-center gap-1">
      <select
        className="eq-input-sm"
        value={current}
        aria-label="Allocated to"
        onChange={(event) => onChange(event.target.value === '' ? null : event.target.value)}
      >
        <option value="">{value?.source === 'unmatched' ? `${value.name}? (unmatched)` : 'Unallocated'}</option>
        {staff.map((person) => (
          <option key={person.id} value={person.id}>
            {person.name}
          </option>
        ))}
      </select>
      {value?.source === 'initials' && value.userId !== null && (
        <button
          type="button"
          className="text-[11px] text-eq-deep underline"
          title="This comes from the imported SmartPractice initials. Confirm it to make it the standing allocation."
          onClick={() => onChange(value.userId)}
        >
          confirm
        </button>
      )}
    </span>
  )
}
